// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeOrderStatus, completeOrder, deleteOrderFiles, selectOrder, selectOrdersIsLoading } from '../../feature';

// Components
import { Button } from '@/ui';
import { PhotoshootingDetails, StyledTabs } from '../../components';
import { StyledContainer, StyledFooter } from './PhotoshootingContainerStyles';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { EntityType } from '@/models/logs.model';
import { OrderStatus } from '@/models/order.model';
import { FileType } from '@/models/uploads.model';

// Modules
import { LogsListContainer } from '@/modules/Logs';

// Helpers
import { getEntityId, toNumber } from '@/utils';
import { useUploadOrderFiles } from '../../hooks';

const PhotoshootingDetailsContainer = () => {
  const { t } = useTranslation('order');
  const dispatch = useAppDispatch();
  const isLoading = useAppState(selectOrdersIsLoading);
  const order = useAppState(selectOrder);
  const { isUploading, uploadFiles } = useUploadOrderFiles();

  const handleChangeStatus = (status: OrderStatus) =>
    dispatch(changeOrderStatus({ id: getEntityId(order?.PK ?? ''), status }));

  const handleUploadFile = async (value: File[], type: FileVariant) => {
    if (!value?.length) return;
    const files = value.map((file) => ({ file, type: FileType.IMAGE }));
    uploadFiles({ orderId: getEntityId(order?.PK ?? ''), files, type });
  };

  const handleDelete = (file: IFile) => {
    if (!file?.id) return;
    dispatch(
      deleteOrderFiles({
        orderId: getEntityId(order?.PK ?? ''),
        fileId: file.id,
      }),
    );
  };

  const handleCompleteOrder = () => dispatch(completeOrder({ id: getEntityId(order?.PK ?? '') }));

  if (!order) return null;

  const canCompleteOrder =
    (!!order?.photos_inside?.length || !!order?.photos_outside?.length) && order.status === OrderStatus.IN_PROGRESS;

  return (
    <StyledTabs
      destroyInactiveTabPane
      items={[
        {
          key: 'details',
          label: t('details'),
          children: (
            <>
              <StyledContainer>
                <PhotoshootingDetails
                  isLoading={isLoading || isUploading}
                  order={order}
                  onChangeStatus={handleChangeStatus}
                  onDelete={handleDelete}
                  onUploadFile={handleUploadFile}
                />
              </StyledContainer>
              {canCompleteOrder && (
                <StyledFooter>
                  <Button type="primary" shape="round" onClick={handleCompleteOrder}>
                    {t('send_photos', {
                      count: toNumber(order?.photos_inside?.length) + toNumber(order?.photos_outside?.length),
                    })}
                  </Button>
                </StyledFooter>
              )}
            </>
          ),
        },
        {
          key: 'logs',
          label: t('logs'),
          children: <LogsListContainer id={order?.PK ?? ''} entityType={EntityType.ORDER} />,
        },
      ]}
    />
  );
};

export default PhotoshootingDetailsContainer;
